'use client'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import type { Market, EventSummary, LeaderboardList, CasinoList, UserListItem } from '@/lib/api'
import { fmtProbability } from '@/lib/currency'
import { useNavUI } from '@/lib/navContext'
import { matchesQuery, normalizeQuery } from '@/lib/search'

type SearchFilter = 'markets' | 'leaderboards' | 'games' | 'profiles' | 'pages' | null

interface PageEntry {
  key: string
  emoji: string
  name: string
  keywords: string[]
  route: string
  adminOnly?: boolean
}

const PAGES: PageEntry[] = [
  { key: 'home',        emoji: '🏠', name: 'Home',                keywords: ['feed', 'home'],                         route: '/' },
  { key: 'shop',        emoji: '🛒', name: 'Shop',                keywords: ['buy', 'crown', 'store'],                route: '/shop' },
  { key: 'gift',        emoji: '🎁', name: 'Bribes',                keywords: ['send', 'transfer', 'cheese', 'bribe', 'gift'],           route: '/gift' },
  { key: 'loans',       emoji: '🐀', name: 'Loans',               keywords: ['loan', 'borrow', 'gorgonzola', 'gambino', 'collateral', 'credit'], route: '/loans' },
  { key: 'unemployment',emoji: '🧀', name: 'Unemployment Form',   keywords: ['benefit', 'bailout', 'broke', 'poor', 'money', 'cheddar', 'welfare'], route: '/unemployment' },
  { key: 'fantasy-dungeon-league', emoji: '🧙', name: 'Fantasy Dungeon League', keywords: ['fantasy', 'dungeon', 'league', 'fdl', 'multiplayer'], route: '/fantasy-dungeon-league' },
  { key: 'dinos',       emoji: '🦖', name: 'Dino Garage',         keywords: ['dino', 'garage', 'ark', 'collectibles'],route: '/collectibles?tab=dinos' },
  { key: 'cards',       emoji: '🃏', name: 'Card Binder',         keywords: ['card', 'binder', 'collectibles', 'pack'],route: '/collectibles?tab=binder' },
  { key: 'settings',    emoji: '⚙️', name: 'Settings',            keywords: ['account', 'profile', 'avatar', 'preferences'], route: '/settings' },
  { key: 'notifs',      emoji: '🔔', name: 'Notification Settings',keywords: ['notifications', 'alerts', 'settings'], route: '/settings?tab=notifications' },
  { key: 'dev',         emoji: '🔑', name: 'Developer Settings',  keywords: ['api', 'token', 'developer', 'key'],     route: '/settings?tab=developer' },
  { key: 'about',       emoji: '📐', name: 'About',               keywords: ['info', 'rules', 'how'],                 route: '/about' },
  { key: 'credits',     emoji: '🎬', name: 'Credits',             keywords: ['credits', 'team'],                      route: '/credits' },
  { key: 'admin',       emoji: '🛠️', name: 'Admin Panel',         keywords: ['admin', 'panel', 'manage'],             route: '/admin', adminOnly: true },
]

const SECTION_LIMIT = 7

// Below this, NavRow1's desktop search bar isn't anchored to anything visible, so the bottom
// nav's Search button opens a full-screen sheet instead of the anchored dropdown. Read via
// useSyncExternalStore (the recommended way to subscribe to an external, mutable browser API)
// rather than useState+useEffect, so there's no synchronous setState-in-effect on mount.
const MOBILE_QUERY = '(max-width: 639.98px)'
function subscribeIsMobile(callback: () => void) {
  const mq = window.matchMedia(MOBILE_QUERY)
  mq.addEventListener('change', callback)
  return () => mq.removeEventListener('change', callback)
}
function getIsMobileSnapshot() {
  return window.matchMedia(MOBILE_QUERY).matches
}
function getIsMobileServerSnapshot() {
  return false
}

const ROW_CLASS = 'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0'
const ROW_CLASS_ACTIVE = ROW_CLASS + ' bg-indigo-50 dark:bg-indigo-900/20'

const SearchIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
  </svg>
)

const CloseIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
  </svg>
)

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <strong className="font-semibold text-gray-900 dark:text-white">{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  )
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
      {label}
    </div>
  )
}

interface NavSearchProps {
  markets: Market[]
  events: EventSummary[]
  currentUserId: number | null
  leaderboardList: LeaderboardList
  casinoList: CasinoList
  users: UserListItem[]
  isAdmin: boolean
}

export default function NavSearch({ markets, events, currentUserId, leaderboardList, casinoList, users, isAdmin }: NavSearchProps) {
  const [query, setQuery] = useState('')
  const [localOpen, setLocalOpen] = useState(false)
  const [filter, setFilter] = useState<SearchFilter>(null)
  const [showMore, setShowMore] = useState<Set<string>>(new Set())
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const isMobile = useSyncExternalStore(subscribeIsMobile, getIsMobileSnapshot, getIsMobileServerSnapshot)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const { searchOpen, closeSearch } = useNavUI()

  const open = localOpen || searchOpen

  const updateRect = useCallback(() => {
    if (containerRef.current) setDropdownRect(containerRef.current.getBoundingClientRect())
  }, [])

  useEffect(() => {
    if (!open) return
    updateRect()
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect)
    }
  }, [open, updateRect])

  useEffect(() => {
    if (searchOpen) setTimeout(() => inputRef.current?.focus(), 50)
  }, [searchOpen])

  // Cross-root open signal: GlobalShortcuts may run in a separate React root (the casino's
  // shortcuts widget, which has no NavUIProvider in scope), so it opens search via a window
  // event rather than context. Listening here directly means '/' works whether or not this
  // instance shares a provider tree with whatever dispatched it.
  useEffect(() => {
    function onOpenSearch() {
      setLocalOpen(true)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
    window.addEventListener('weeabets:open-search', onOpenSearch)
    return () => window.removeEventListener('weeabets:open-search', onOpenSearch)
  }, [])

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node
      const inContainer = containerRef.current?.contains(target) ?? false
      const inDropdown = dropdownRef.current?.contains(target) ?? false
      if (!inContainer && !inDropdown) {
        setLocalOpen(false)
        closeSearch()
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [closeSearch])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setLocalOpen(false)
        closeSearch()
        if (document.activeElement === inputRef.current) inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closeSearch])


  const q = normalizeQuery(query)

  const marketResults = useMemo(() => {
    if (!q) return []
    return markets.filter(m =>
      m.status === 'open'
      && !(currentUserId && (m.is_inside_joke
        ? m.restricted_user_ids.length > 0 && !m.restricted_user_ids.includes(currentUserId)
        : m.restricted_user_ids.includes(currentUserId)))
      && (m.question.toLowerCase().includes(q) || m.categories.some(c => c.toLowerCase().includes(q)))
    )
  }, [q, markets, currentUserId])

  const eventResults = useMemo(() => {
    if (!q) return []
    return events.filter(e =>
      e.status === 'open'
      && (e.question.toLowerCase().includes(q) || e.categories.some(c => c.toLowerCase().includes(q)))
    )
  }, [q, events])

  const leaderboardResults = useMemo(() => {
    if (!q) return []
    return leaderboardList.boards.filter(b => matchesQuery(q, [b.name, b.alt_name, ...b.keywords]))
  }, [q, leaderboardList])

  const gameResults = useMemo(() => {
    if (!q) return []
    return casinoList.games.filter(g =>
      [g.name, g.alt_name, ...g.keywords].some(s => s?.toLowerCase().includes(q))
    )
  }, [q, casinoList])

  const profileResults = useMemo(() => {
    if (!q) return []
    return users.filter(u =>
      u.username.toLowerCase().includes(q) || u.discord_username.toLowerCase().includes(q)
    )
  }, [q, users])

  const pageResults = useMemo(() => {
    if (!q) return []
    return PAGES.filter(p => {
      if (p.adminOnly && !isAdmin) return false
      return p.name.toLowerCase().includes(q) || p.keywords.some(k => k.includes(q))
    })
  }, [q, isAdmin])

  // Flat ordered list of all rendered result items — mirrors renderResults order for arrow key nav
  const flatItems = useMemo<{ key: string; href: string }[]>(() => {
    if (!q) return []
    const items: { key: string; href: string }[] = []

    if (filter === null || filter === 'markets') {
      const all = [
        ...marketResults.map(m => ({ key: `m-${m.id}`, href: `/markets/${m.id}` })),
        ...eventResults.map(e => ({ key: `e-${e.id}`, href: `/events/${e.id}` })),
      ]
      const shown = showMore.has('markets') ? all : all.slice(0, SECTION_LIMIT)
      items.push(...shown)
    }

    if (filter === null || filter === 'leaderboards') {
      const shown = showMore.has('leaderboards') ? leaderboardResults : leaderboardResults.slice(0, SECTION_LIMIT)
      items.push(...shown.map(b => ({ key: `lb-${b.key}`, href: b.route })))
    }

    if (filter === null || filter === 'games') {
      const shown = showMore.has('games') ? gameResults : gameResults.slice(0, SECTION_LIMIT)
      items.push(...shown.map(g => ({ key: `g-${g.key}`, href: g.route })))
    }

    if (filter === null || filter === 'profiles') {
      const shown = showMore.has('profiles') ? profileResults : profileResults.slice(0, SECTION_LIMIT)
      items.push(...shown.map(u => ({ key: `u-${u.id}`, href: `/profile/${encodeURIComponent(u.discord_username)}` })))
    }

    if (filter === null || filter === 'pages') {
      items.push(...pageResults.map(p => ({ key: `p-${p.key}`, href: p.route })))
    }

    return items
  }, [q, filter, showMore, marketResults, eventResults, leaderboardResults, gameResults, profileResults, pageResults])

  const activeKey = flatItems[activeIndex]?.key ?? null

  // Scroll active item into view when index changes
  useEffect(() => {
    if (activeIndex < 0) return
    const key = flatItems[activeIndex]?.key
    if (key) document.getElementById(`si-${key}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, flatItems])

  function select(href: string) {
    setQuery('')
    setActiveIndex(-1)
    setLocalOpen(false)
    closeSearch()
    router.push(href)
  }

  function toggleFilter(key: Exclude<SearchFilter, null>) {
    setFilter(prev => prev === key ? null : key)
    setActiveIndex(-1)
  }

  function toggleShowMore(section: string, e: React.MouseEvent) {
    e.preventDefault()
    setShowMore(prev => {
      const next = new Set(prev)
      if (next.has(section)) { next.delete(section) } else { next.add(section) }
      return next
    })
  }

  const FILTERS: { key: Exclude<SearchFilter, null>; label: string }[] = [
    { key: 'markets',      label: 'Markets' },
    { key: 'leaderboards', label: 'Leaderboards' },
    { key: 'games',        label: 'Games' },
    { key: 'profiles',     label: 'Profiles' },
    { key: 'pages',        label: 'Pages' },
  ]

  function renderShowMore(section: string, total: number, shown: number) {
    const remaining = total - shown
    if (remaining <= 0) return null
    return (
      <button
        onMouseDown={e => toggleShowMore(section, e)}
        className="w-full px-4 py-2.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline text-left border-b border-gray-100 dark:border-gray-800 last:border-0"
      >
        Show {remaining} more
      </button>
    )
  }

  function renderMarketsSection(withHeader: boolean) {
    const all = [
      ...marketResults.map(m => ({ kind: 'market' as const, m })),
      ...eventResults.map(e => ({ kind: 'event' as const, e })),
    ]
    if (all.length === 0) return null
    const expanded = showMore.has('markets')
    const shown = expanded ? all : all.slice(0, SECTION_LIMIT)
    return (
      <div key="markets-section">
        {withHeader && <SectionHeader label="Markets" />}
        {shown.map(item => item.kind === 'market' ? (
          <button
            key={`m-${item.m.id}`}
            id={`si-m-${item.m.id}`}
            onMouseDown={() => select(`/markets/${item.m.id}`)}
            className={activeKey === `m-${item.m.id}` ? ROW_CLASS_ACTIVE : ROW_CLASS}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 leading-snug">
                <HighlightMatch text={item.m.question} query={q} />
              </p>
              {item.m.categories.length > 0 && (
                <p className="mt-0.5 text-[10px] text-indigo-500">{item.m.categories.slice(0, 3).join(' · ')}</p>
              )}
            </div>
            <div className="shrink-0 text-right ml-3">
              {item.m.status === 'resolved' ? (
                <span className={`text-xs font-bold ${item.m.outcome === 'yes' ? 'text-green-500' : 'text-red-500'}`}>
                  {item.m.outcome === 'yes' ? '✓ YES' : '✗ NO'}
                </span>
              ) : item.m.status === 'locked' ? (
                <span className="text-xs font-bold text-amber-500">Locked</span>
              ) : (
                <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{Math.round(item.m.yes_prob * 100)}%</span>
              )}
            </div>
          </button>
        ) : (
          <button
            key={`e-${item.e.id}`}
            id={`si-e-${item.e.id}`}
            onMouseDown={() => select(`/events/${item.e.id}`)}
            className={activeKey === `e-${item.e.id}` ? ROW_CLASS_ACTIVE : ROW_CLASS}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 leading-snug">
                <HighlightMatch text={item.e.question} query={q} />
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] font-bold text-purple-500 uppercase tracking-wide">Event</span>
                {item.e.categories.length > 0 && (
                  <span className="text-[10px] text-indigo-500">· {item.e.categories.slice(0, 2).join(' · ')}</span>
                )}
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-0.5 ml-3">
              {item.e.markets.slice(0, 2).map((m, idx) => (
                <span key={idx} className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">{m.label} {fmtProbability(m.yes_prob)}</span>
              ))}
            </div>
          </button>
        ))}
        {renderShowMore('markets', all.length, shown.length)}
      </div>
    )
  }

  function renderLeaderboardsSection(withHeader: boolean) {
    if (leaderboardResults.length === 0) return null
    const expanded = showMore.has('leaderboards')
    const shown = expanded ? leaderboardResults : leaderboardResults.slice(0, SECTION_LIMIT)
    return (
      <div key="leaderboards-section">
        {withHeader && <SectionHeader label="Leaderboards" />}
        {shown.map(b => (
          <button
            key={b.key}
            id={`si-lb-${b.key}`}
            onMouseDown={() => select(b.route)}
            className={activeKey === `lb-${b.key}` ? ROW_CLASS_ACTIVE : ROW_CLASS}
          >
            <span className="text-lg shrink-0">{b.emoji}</span>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              <HighlightMatch text={b.name} query={q} />
            </span>
          </button>
        ))}
        {renderShowMore('leaderboards', leaderboardResults.length, shown.length)}
      </div>
    )
  }

  function renderGamesSection(withHeader: boolean) {
    if (gameResults.length === 0) return null
    const expanded = showMore.has('games')
    const shown = expanded ? gameResults : gameResults.slice(0, SECTION_LIMIT)
    return (
      <div key="games-section">
        {withHeader && <SectionHeader label="Games" />}
        {shown.map(g => (
          <button
            key={g.key}
            id={`si-g-${g.key}`}
            onMouseDown={() => select(g.route)}
            className={activeKey === `g-${g.key}` ? ROW_CLASS_ACTIVE : ROW_CLASS}
          >
            <span className="text-lg shrink-0">{g.emoji}</span>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              <HighlightMatch text={g.name} query={q} />
            </span>
          </button>
        ))}
        {renderShowMore('games', gameResults.length, shown.length)}
      </div>
    )
  }

  function renderProfilesSection(withHeader: boolean) {
    if (profileResults.length === 0) return null
    const expanded = showMore.has('profiles')
    const shown = expanded ? profileResults : profileResults.slice(0, SECTION_LIMIT)
    return (
      <div key="profiles-section">
        {withHeader && <SectionHeader label="Profiles" />}
        {shown.map(u => (
          <button
            key={u.id}
            id={`si-u-${u.id}`}
            onMouseDown={() => select(`/profile/${encodeURIComponent(u.discord_username)}`)}
            className={activeKey === `u-${u.id}` ? ROW_CLASS_ACTIVE : ROW_CLASS}
          >
            <div className="w-8 h-8 rounded-full overflow-hidden bg-indigo-600 shrink-0">
              <Image src={u.avatar_url} alt="" width={32} height={32} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                <HighlightMatch text={u.username} query={q} />
              </p>
              {u.username !== u.discord_username && (
                <p className="text-xs text-gray-400 truncate">@{u.discord_username}</p>
              )}
            </div>
          </button>
        ))}
        {renderShowMore('profiles', profileResults.length, shown.length)}
      </div>
    )
  }

  function renderPagesSection(withHeader: boolean) {
    if (pageResults.length === 0) return null
    return (
      <div key="pages-section">
        {withHeader && <SectionHeader label="Pages" />}
        {pageResults.map(p => (
          <button
            key={p.key}
            id={`si-p-${p.key}`}
            onMouseDown={() => select(p.route)}
            className={activeKey === `p-${p.key}` ? ROW_CLASS_ACTIVE : ROW_CLASS}
          >
            <span className="text-lg shrink-0">{p.emoji}</span>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              <HighlightMatch text={p.name} query={q} />
            </span>
          </button>
        ))}
      </div>
    )
  }

  function renderFilterChips() {
    return (
      <div className="flex gap-2 px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onMouseDown={e => { e.preventDefault(); toggleFilter(f.key) }}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              filter === f.key
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
    )
  }

  function renderResults() {
    if (!q) {
      return <p className="py-8 text-center text-sm text-gray-400">Start typing to search</p>
    }

    if (filter === 'markets') {
      const section = renderMarketsSection(false)
      return section ?? <p className="py-8 text-center text-sm text-gray-400">No markets found</p>
    }
    if (filter === 'leaderboards') {
      const section = renderLeaderboardsSection(false)
      return section ?? <p className="py-8 text-center text-sm text-gray-400">No leaderboards found</p>
    }
    if (filter === 'games') {
      const section = renderGamesSection(false)
      return section ?? <p className="py-8 text-center text-sm text-gray-400">No games found</p>
    }
    if (filter === 'profiles') {
      const section = renderProfilesSection(false)
      return section ?? <p className="py-8 text-center text-sm text-gray-400">No profiles found</p>
    }
    if (filter === 'pages') {
      const section = renderPagesSection(false)
      return section ?? <p className="py-8 text-center text-sm text-gray-400">No pages found</p>
    }

    // All categories
    const sections = [
      renderMarketsSection(true),
      renderLeaderboardsSection(true),
      renderGamesSection(true),
      renderProfilesSection(true),
      renderPagesSection(true),
    ].filter(Boolean)

    if (sections.length === 0) {
      return <p className="py-8 text-center text-sm text-gray-400">No results found</p>
    }
    return <>{sections}</>
  }

  const dropdownStyle = dropdownRect ? {
    position: 'fixed' as const,
    top: dropdownRect.bottom + 8,
    left: dropdownRect.left,
    width: dropdownRect.width,
    zIndex: 9999,
  } : undefined

  const searchInputProps = {
    ref: inputRef,
    type: 'text' as const,
    value: query,
    placeholder: 'Search…',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => { setQuery(e.target.value); setShowMore(new Set()); setActiveIndex(-1); setLocalOpen(true) },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(i => i < flatItems.length - 1 ? i + 1 : i)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(i => i > -1 ? i - 1 : -1)
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        const item = flatItems[activeIndex]
        if (item) select(item.href)
      }
    },
  }

  // Below `sm`, the desktop bar isn't anchored to anything visible (NavRow1 hides that whole
  // middle section on mobile), so render a full-screen sheet instead — portaled to <body> so
  // it isn't clipped by that hidden ancestor either. Closed on mobile just renders nothing.
  if (isMobile) {
    if (!open) return null
    return createPortal(
      <div ref={dropdownRef} className="fixed inset-0 z-[100] flex flex-col bg-white dark:bg-gray-950">
        <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-800 px-3 py-3">
          <SearchIcon />
          <input {...searchInputProps} className="w-full bg-transparent text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none" />
          <button
            onClick={() => { setQuery(''); setActiveIndex(-1); setLocalOpen(false); closeSearch() }}
            aria-label="Close search"
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <CloseIcon />
          </button>
        </div>
        {renderFilterChips()}
        <div className="flex-1 overflow-y-auto">
          {renderResults()}
        </div>
      </div>,
      document.body
    )
  }

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <div className={`flex items-center gap-2 rounded-full border bg-gray-50 dark:bg-gray-900 px-4 py-2 w-full transition-colors ${open ? 'border-indigo-400 dark:border-indigo-600' : 'border-gray-200 dark:border-gray-700'}`}>
        <SearchIcon />
        <input {...searchInputProps} onFocus={() => setLocalOpen(true)} className="w-full bg-transparent text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none" />
        {(query || open) && (
          <button
            onClick={() => { setQuery(''); setActiveIndex(-1); setLocalOpen(false); closeSearch() }}
            className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {open && dropdownStyle && createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
          {renderFilterChips()}

          {/* Results */}
          <div className="max-h-[32rem] overflow-y-auto">
            {renderResults()}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
